/* ──────────────────────────────────────────────────────────────────────────
 * CA Firm Dashboard — "My Clients" portal for a registered CA firm.
 * Faithful port of the provided dashboard-clients / dashboard-empty mockups.
 * Firm identity is pulled from /api/ca-firm; clients are real, persisted rows
 * (add / edit / import from Excel-CSV / delete). Each client can be promoted
 * into its OWN isolated IPO workspace via "Take me to IPO" (sets the active
 * workspace server-side, then lands in the IPO Navigator). Scoped under `.cafd`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./CAFirmDashboard.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXCHANGES = ["Not decided", "BSE SME", "NSE Emerge", "NSE Main Board", "BSE Main Board"];
const ENTITY_TYPES = ["Private Limited", "Public Limited", "LLP"];

const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter((w) => /[a-zA-Z0-9]/.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "CL";

const crd = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^\d.\-]/g, ""));
  return Number.isFinite(n) && n !== 0 ? `\u20B9${v} Cr` : "\u2014";
};
const pct = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^\d.\-]/g, ""));
  return Number.isFinite(n) && n !== 0 ? `${v}%` : "\u2014";
};
const STATUS_LABEL = { ready: "IPO Ready", near: "Near Ready", early: "Early Stage" };
const scoreColor = (status) =>
  status === "ready" ? "#1e7a3d" : status === "near" ? "#9a6b0a" : "#7c5cfc";

const relTime = (iso) => {
  if (!iso) return "\u2014";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "\u2014";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "1 day ago";
  if (d < 14) return `${d} days ago`;
  const w = Math.floor(d / 7);
  return `${w} week${w > 1 ? "s" : ""} ago`;
};

const BLANK_CLIENT = {
  company_name: "", sector: "", contact_person: "", contact: "",
  entity_type: "Private Limited", target_exchange: "Not decided",
  net_worth_latest: "", net_worth_prior: "", net_tangible_assets: "",
  ebitda_3yr: "", total_debt: "", promoter_holding: "",
  track_record_3yr: true, regulatory_action: false, promoter_change: false, demat_100: true,
};

const CAFirmDashboard = () => {
  const navigate = useNavigate();
  const [firm, setFirm] = useState(null);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ total: 0, ready: 0, near: 0, early: 0 });
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ca-firm`, { credentials: "include" });
      const data = res.ok ? await res.json() : null;
      setFirm(data?.firm || null);
      setClients(data?.clients || []);
      setStats(data?.stats || { total: 0, ready: 0, near: 0, early: 0 });
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Browser Back on this landing page returns the user to the IPO role-select
  // screen (kept logged in — client-side SPA navigation, no session change).
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const onPop = () => navigate("/ipo-role-select", { replace: true });
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [navigate]);

  const openClient = async (c) => {
    setOpeningId(c.id);
    try {
      const res = await fetch(`${API_URL}/api/ca-firm/clients/${c.id}/open`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error();
      toast.success(`Opening ${c.company_name} in the IPO Navigator…`);
      navigate("/ipo-navigator");
    } catch {
      toast.error("Could not open this client's IPO workspace. Please try again.");
      setOpeningId(null);
    }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Remove ${c.company_name} from your roster?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/ca-firm/clients/${c.id}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) throw new Error();
      toast.success("Client removed.");
      load();
    } catch {
      toast.error("Could not remove this client.");
    }
  };

  const editingClient = editId ? clients.find((c) => c.id === editId) : null;
  const firmName = firm?.firm_name || "Your CA firm";
  const firmContact = [firm?.poc_name, firm?.email].filter(Boolean).join(" \u00B7 ");

  return (
    <div className="cafd" data-testid="ca-firm-dashboard-page">
      {/* Top nav */}
      <nav className="cafd-nav">
        <div className="wrap cafd-nav-inner">
          <button className="cafd-logo" onClick={() => navigate("/ipo-role-select")} data-testid="cafd-brand">
            <span className="mark">IL</span> IPOLabs
          </button>
          <div className="cafd-tabs">
            <button className="active" onClick={() => navigate("/home")} data-testid="cafd-home">
              <i className="ti ti-home" aria-hidden="true" /> Home Page
            </button>
            <button onClick={() => navigate("/oracle")} data-testid="cafd-oracle">
              <i className="ti ti-robot" aria-hidden="true" /> Oracle
            </button>
          </div>
          <div className="cafd-firm" data-testid="cafd-firm-chip">
            <div className="cafd-firm-badge">{initials(firmName)}</div>
            <div>
              <div className="cafd-firm-name" data-testid="cafd-firm-name">{firmName}</div>
              {firmContact && <div className="cafd-firm-contact">{firmContact}</div>}
            </div>
          </div>
        </div>
      </nav>

      {loading ? (
        <div className="cafd-loading" data-testid="cafd-loading">Loading your client roster…</div>
      ) : clients.length === 0 ? (
        <div className="wrap">
          <div className="cafd-empty-wrap" data-testid="cafd-empty-state">
            <div className="cafd-empty-card">
              <div className="cafd-empty-icon"><i className="ti ti-clipboard-list" aria-hidden="true" /></div>
              <h2>No clients added yet</h2>
              <p>
                Add your clients to start tracking their IPO readiness — net worth, track record,
                compliance and more, all in one dashboard.
              </p>
              <div className="cafd-empty-actions">
                <button className="cafd-btn cafd-btn-primary" onClick={() => { setEditId(null); setAddOpen(true); }} data-testid="cafd-empty-add-client">
                  <i className="ti ti-plus" aria-hidden="true" /> Add Client
                </button>
                <button className="cafd-btn cafd-btn-outline" onClick={() => setUploadOpen(true)} data-testid="cafd-empty-upload">
                  <i className="ti ti-upload" aria-hidden="true" /> Upload Excel / CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="wrap">
          <div className="cafd-page-head">
            <div>
              <h1>My Clients</h1>
              <p>Track IPO readiness across every client, in one place.</p>
            </div>
            <div className="cafd-head-actions">
              <button className="cafd-btn cafd-btn-outline" onClick={() => setUploadOpen(true)} data-testid="cafd-upload-excel">
                <i className="ti ti-upload" aria-hidden="true" /> Upload Excel
              </button>
              <button className="cafd-btn cafd-btn-primary" onClick={() => { setEditId(null); setAddOpen(true); }} data-testid="cafd-add-client">
                <i className="ti ti-plus" aria-hidden="true" /> Add Client
              </button>
            </div>
          </div>

          <div className="cafd-stat-row">
            <div className="cafd-stat-card"><div className="lbl">Total clients</div><div className="val" data-testid="cafd-stat-total">{stats.total}</div></div>
            <div className="cafd-stat-card"><div className="lbl">IPO ready</div><div className="val ready" data-testid="cafd-stat-ready">{stats.ready}</div></div>
            <div className="cafd-stat-card"><div className="lbl">Near ready</div><div className="val near" data-testid="cafd-stat-near">{stats.near}</div></div>
            <div className="cafd-stat-card"><div className="lbl">Early stage</div><div className="val" data-testid="cafd-stat-early">{stats.early}</div></div>
          </div>

          <div className="cafd-table-card">
            <table data-testid="cafd-client-table">
              <thead>
                <tr>
                  <th>Client</th><th>Target exchange</th><th>Net worth</th>
                  <th>EBITDA (3yr avg)</th><th>Track record</th><th>Promoter holding</th>
                  <th>Readiness score</th><th>Status</th><th>Updated</th><th />
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id} data-testid={`cafd-client-row-${c.id}`}>
                    <td>
                      <div className="cafd-client-cell">
                        <div className="cafd-client-avatar">{initials(c.company_name)}</div>
                        <div>
                          <div className="cafd-client-name">{c.company_name}</div>
                          <div className="cafd-client-sub">{c.sector || "\u2014"}</div>
                        </div>
                      </div>
                    </td>
                    <td>{c.target_exchange || "Not decided"}</td>
                    <td>{crd(c.net_worth_latest)}</td>
                    <td>{crd(c.ebitda_3yr)}</td>
                    <td>{c.track_record_3yr ? "\u2265 3 yrs" : "< 3 yrs"}</td>
                    <td>{pct(c.promoter_holding)}</td>
                    <td>
                      <div className="cafd-score-wrap">
                        <div className="cafd-score-bar">
                          <div style={{ width: `${c.readiness_score}%`, background: scoreColor(c.status) }} />
                        </div>
                        {c.readiness_score}
                      </div>
                    </td>
                    <td><span className={`cafd-badge ${c.status}`}>{STATUS_LABEL[c.status]}</span></td>
                    <td>{relTime(c.updated_at)}</td>
                    <td>
                      <div className="cafd-row-actions">
                        <button className="cafd-row-view" onClick={() => { setEditId(c.id); setAddOpen(true); }} data-testid={`cafd-client-view-${c.id}`}>
                          View <i className="ti ti-arrow-right" aria-hidden="true" />
                        </button>
                        <button
                          className="cafd-row-ipo"
                          onClick={() => openClient(c)}
                          disabled={openingId === c.id}
                          data-testid={`cafd-client-take-to-ipo-${c.id}`}
                          title="Open this client in the IPO Navigator"
                        >
                          <i className="ti ti-rocket" aria-hidden="true" />
                          {openingId === c.id ? "Opening…" : "Take me to IPO"}
                        </button>
                        <button className="cafd-row-del" onClick={() => removeClient(c)} data-testid={`cafd-client-delete-${c.id}`} title="Remove client">
                          <i className="ti ti-trash" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {addOpen && (
        <ClientModal
          initial={editingClient}
          onClose={() => { setAddOpen(false); setEditId(null); }}
          onSaved={() => { setAddOpen(false); setEditId(null); load(); }}
        />
      )}
      {uploadOpen && (
        <UploadModal onClose={() => setUploadOpen(false)} onImported={() => { setUploadOpen(false); load(); }} />
      )}
    </div>
  );
};

/* ───────────────────────────── Add / Edit modal ───────────────────────────── */
const Toggle = ({ label, value, onChange, testid }) => (
  <div className="cafd-field-toggle">
    {label}
    <div className="cafd-toggle-btns">
      <button type="button" className={value ? "sel" : ""} onClick={() => onChange(true)} data-testid={`${testid}-yes`}>Yes</button>
      <button type="button" className={!value ? "sel" : ""} onClick={() => onChange(false)} data-testid={`${testid}-no`}>No</button>
    </div>
  </div>
);

const ClientModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [f, setF] = useState({ ...BLANK_CLIENT, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.company_name.trim()) { toast.error("Please enter the company name."); return; }
    setSaving(true);
    const body = {
      company_name: f.company_name, sector: f.sector, contact_person: f.contact_person,
      contact: f.contact, entity_type: f.entity_type, target_exchange: f.target_exchange,
      net_worth_latest: f.net_worth_latest, net_worth_prior: f.net_worth_prior,
      net_tangible_assets: f.net_tangible_assets, ebitda_3yr: f.ebitda_3yr,
      total_debt: f.total_debt, promoter_holding: f.promoter_holding,
      track_record_3yr: !!f.track_record_3yr, regulatory_action: !!f.regulatory_action,
      promoter_change: !!f.promoter_change, demat_100: !!f.demat_100,
    };
    try {
      const url = isEdit
        ? `${API_URL}/api/ca-firm/clients/${initial.id}`
        : `${API_URL}/api/ca-firm/clients`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = "Could not save this client. Please try again.";
        try { const d = await res.json(); if (typeof d.detail === "string") msg = d.detail; } catch { /* ignore */ }
        toast.error(msg);
        setSaving(false);
        return;
      }
      toast.success(isEdit ? "Client updated." : "Client added.");
      onSaved();
    } catch {
      toast.error("Could not save this client. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="cafd-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cafd-modal" data-testid="cafd-client-modal">
        <div className="cafd-modal-head">
          <h3>{isEdit ? "Edit client" : "Add a client"}</h3>
          <button className="cafd-modal-close" onClick={onClose} data-testid="cafd-client-modal-close"><i className="ti ti-x" aria-hidden="true" /></button>
        </div>
        <p className="cafd-modal-sub">Enter the details below to track this client&apos;s IPO readiness.</p>

        <div className="cafd-form-section">
          <div className="cafd-sec-title">Basic details</div>
          <div className="cafd-form-grid">
            <div className="cafd-field"><label>Company name</label><input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Enter company name" data-testid="cafd-f-company-name" /></div>
            <div className="cafd-field"><label>Sector / industry</label><input value={f.sector} onChange={(e) => set("sector", e.target.value)} placeholder="e.g. Auto ancillary" data-testid="cafd-f-sector" /></div>
            <div className="cafd-field"><label>Contact person</label><input value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="Promoter / CFO name" data-testid="cafd-f-contact-person" /></div>
            <div className="cafd-field"><label>Contact email or mobile</label><input value={f.contact} onChange={(e) => set("contact", e.target.value)} placeholder="Email or 10-digit mobile" data-testid="cafd-f-contact" /></div>
            <div className="cafd-field"><label>Entity type</label>
              <select value={f.entity_type} onChange={(e) => set("entity_type", e.target.value)} data-testid="cafd-f-entity-type">
                {ENTITY_TYPES.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div className="cafd-field"><label>Target exchange</label>
              <select value={f.target_exchange} onChange={(e) => set("target_exchange", e.target.value)} data-testid="cafd-f-exchange">
                {EXCHANGES.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="cafd-form-section">
          <div className="cafd-sec-title">Financial metrics</div>
          <div className="cafd-form-grid">
            <div className="cafd-field"><label>Net worth — latest FY (₹ Cr)</label><input value={f.net_worth_latest} onChange={(e) => set("net_worth_latest", e.target.value)} placeholder="e.g. 3.2" data-testid="cafd-f-nw-latest" /></div>
            <div className="cafd-field"><label>Net worth — prior FY (₹ Cr)</label><input value={f.net_worth_prior} onChange={(e) => set("net_worth_prior", e.target.value)} placeholder="e.g. 2.1" /></div>
            <div className="cafd-field"><label>Net tangible assets (₹ Cr)</label><input value={f.net_tangible_assets} onChange={(e) => set("net_tangible_assets", e.target.value)} placeholder="e.g. 3.5" /></div>
            <div className="cafd-field"><label>EBITDA — 3yr average (₹ Cr)</label><input value={f.ebitda_3yr} onChange={(e) => set("ebitda_3yr", e.target.value)} placeholder="e.g. 1.1" data-testid="cafd-f-ebitda" /></div>
            <div className="cafd-field"><label>Total debt (₹ Cr)</label><input value={f.total_debt} onChange={(e) => set("total_debt", e.target.value)} placeholder="For leverage ratio" /></div>
            <div className="cafd-field"><label>Promoter holding post-issue (%)</label><input value={f.promoter_holding} onChange={(e) => set("promoter_holding", e.target.value)} placeholder="e.g. 35" data-testid="cafd-f-promoter" /></div>
          </div>
        </div>

        <div className="cafd-form-section">
          <div className="cafd-sec-title">Compliance</div>
          <div className="cafd-form-grid">
            <Toggle label="Track record ≥ 3 years" value={f.track_record_3yr} onChange={(v) => set("track_record_3yr", v)} testid="cafd-t-track" />
            <Toggle label="Any regulatory action / default" value={f.regulatory_action} onChange={(v) => set("regulatory_action", v)} testid="cafd-t-reg" />
            <Toggle label="Promoter change in last year" value={f.promoter_change} onChange={(v) => set("promoter_change", v)} testid="cafd-t-promoter-change" />
            <Toggle label="100% promoter shares in demat" value={f.demat_100} onChange={(v) => set("demat_100", v)} testid="cafd-t-demat" />
          </div>
        </div>

        <div className="cafd-modal-foot">
          <button className="cafd-btn cafd-btn-outline" onClick={onClose} data-testid="cafd-client-cancel">Cancel</button>
          <button className="cafd-btn cafd-btn-primary" onClick={save} disabled={saving} data-testid="cafd-client-save">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Save Client"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────── Upload modal ─────────────────────────────── */
const UploadModal = ({ onClose, onImported }) => {
  const [stage, setStage] = useState(1); // 1 browse · 2 reading · 3 review
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState({ mapping: [], count: 0, clients: [] });
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setStage(2);
    setProgress(15);
    const timer = setInterval(() => setProgress((p) => Math.min(p + 18, 90)), 220);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_URL}/api/ca-firm/clients/import/preview`, {
        method: "POST", credentials: "include", body: fd,
      });
      clearInterval(timer);
      setProgress(100);
      if (!res.ok) {
        let msg = "Could not read that file.";
        try { const d = await res.json(); if (typeof d.detail === "string") msg = d.detail; } catch { /* ignore */ }
        toast.error(msg);
        setStage(1); setProgress(0);
        return;
      }
      const data = await res.json();
      if (!data.count) {
        toast.error("No clients found in that file. Check the column headers and try again.");
        setStage(1); setProgress(0);
        return;
      }
      setPreview(data);
      setTimeout(() => setStage(3), 350);
    } catch {
      clearInterval(timer);
      toast.error("Could not read that file.");
      setStage(1); setProgress(0);
    }
  };

  const commit = async () => {
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-firm/clients/import/commit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ clients: preview.clients }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      toast.success(`Imported ${d.created} client(s)${d.skipped ? `, ${d.skipped} skipped as duplicates` : ""}.`);
      onImported();
    } catch {
      toast.error("Could not import these clients. Please try again.");
      setImporting(false);
    }
  };

  return (
    <div className="cafd-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cafd-modal" data-testid="cafd-upload-modal">
        <div className="cafd-modal-head">
          <h3>Upload clients</h3>
          <button className="cafd-modal-close" onClick={onClose} data-testid="cafd-upload-close"><i className="ti ti-x" aria-hidden="true" /></button>
        </div>
        <p className="cafd-modal-sub">Upload an Excel (.xlsx) or CSV file — we&apos;ll read the client data automatically.</p>

        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={onFile} data-testid="cafd-upload-input" />

        {stage === 1 && (
          <div className="cafd-dropzone">
            <div className="cafd-dz-icon"><i className="ti ti-cloud-upload" aria-hidden="true" /></div>
            <p className="cafd-dz-main">Choose an Excel or CSV file</p>
            <p className="cafd-dz-sub">Supports .xlsx, .xls and .csv — headers like Company, Net Worth, EBITDA, Promoter %, Exchange are auto-detected.</p>
            <button className="cafd-btn cafd-btn-primary" onClick={() => inputRef.current?.click()} data-testid="cafd-upload-browse">Browse Files</button>
          </div>
        )}

        {stage === 2 && (
          <div className="cafd-dropzone">
            <div className="cafd-dz-icon"><i className="ti ti-file-spreadsheet" aria-hidden="true" /></div>
            <p className="cafd-dz-main">{fileName}</p>
            <p className="cafd-dz-sub">Reading &amp; matching columns…</p>
            <div className="cafd-progress-track"><div style={{ width: `${progress}%` }} /></div>
          </div>
        )}

        {stage === 3 && (
          <div data-testid="cafd-upload-review">
            <p className="cafd-modal-sub" style={{ marginBottom: 12 }}>We matched these columns automatically — review before importing.</p>
            {preview.mapping.map((m, i) => (
              <div className="cafd-map-row" key={i}>
                <span className="src">{m.src}</span>
                <span className="arrow"><i className="ti ti-arrow-right" aria-hidden="true" /></span>
                <span className="dst">{m.dst}</span>
                <span className="cafd-map-ok"><i className="ti ti-check" aria-hidden="true" /></span>
              </div>
            ))}
            <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 14 }} data-testid="cafd-upload-count">
              {preview.count} client{preview.count === 1 ? "" : "s"} detected in this file.
            </p>
          </div>
        )}

        <div className="cafd-modal-foot">
          <button className="cafd-btn cafd-btn-outline" onClick={onClose} data-testid="cafd-upload-cancel">Cancel</button>
          {stage === 3 ? (
            <button className="cafd-btn cafd-btn-primary" onClick={commit} disabled={importing} data-testid="cafd-upload-confirm">
              {importing ? "Importing…" : `Confirm & Import ${preview.count}`}
            </button>
          ) : (
            <button className="cafd-btn cafd-btn-primary" onClick={() => inputRef.current?.click()} disabled={stage === 2} data-testid="cafd-upload-next">
              {stage === 2 ? "Reading…" : "Browse Files"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CAFirmDashboard;
